// src/pages/ProductDetail.jsx

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useCart } from '../context/CartContext';
import { productService } from '../services/api'; // Assuming you have this service
import EnhancedLoginModal from '../components/EnhancedLoginModal';
import './ProductDetail.css';

const ProductDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { addToCart } = useCart();

  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [quantity, setQuantity] = useState(1);
  const [selectedImage, setSelectedImage] = useState(0);
  const [user, setUser] = useState(null);
  const [showLoginModal, setShowLoginModal] = useState(false);


  useEffect(() => {
    fetchProductDetails();
  }, [id]);

  // Check user authentication status
  useEffect(() => {
    const savedUser = localStorage.getItem('user');
    if (savedUser) {
      try {
        const userData = JSON.parse(savedUser);
        setUser(userData);
      } catch (e) {
        console.error('Error parsing saved user:', e);
      }
    }
  }, []);

  const fetchProductDetails = async () => {
    try {
      setLoading(true);
      setError(null);
      
      console.log('Fetching product details for ID:', id);
      
      // Fetch product details from your API
      const detailedProduct = await productService.getProduct(id);
      
      console.log('Product details received:', detailedProduct);
      
      // Process the product data similar to your QuickViewModal
      const processedProduct = processProductData(detailedProduct);
      
      setProduct(processedProduct);
    } catch (err) {
      console.error('Error fetching product details:', err);
      setError('Failed to load product details. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const processProductData = (apiProduct) => {
    // Process images
    const primaryImageObj = apiProduct.primary_image;
    const imagesArray = apiProduct.images || [];
    
    // Extract primary image URL
    const primaryImageUrl = primaryImageObj?.image_url || 
                          '/images/placeholder.jpg';
    
    // Extract additional images
    let additionalImages = [];
    if (Array.isArray(imagesArray) && imagesArray.length > 0) {
      const allImageUrls = imagesArray
        .filter(img => img && img.image_url)
        .map(img => img.image_url);
      additionalImages = allImageUrls.filter(url => url !== primaryImageUrl);
    }

    // Get brand name
    const brandName = apiProduct.brand?.name || 'Unknown Brand';

    // Price calculations
    const currentPrice = Number(apiProduct.current_price) || 0;
    const hasDiscount = !!apiProduct.has_discount;
    const discountPercentage = apiProduct.discount_percentage || 0;
    
    // Calculate original price
    let originalPrice = currentPrice;
    if (hasDiscount && discountPercentage > 0) {
      originalPrice = currentPrice / (1 - discountPercentage / 100);
    }

    return {
      id: apiProduct.id,
      name: apiProduct.name,
      productname: apiProduct.name,
      sku: apiProduct.sku,
      brand: brandName,
      price: originalPrice,
      discountprice: hasDiscount ? currentPrice : 0,
      current_price: currentPrice,
      has_discount: hasDiscount,
      discount_percentage: discountPercentage,
      stock_status: apiProduct.stock_status,
      stock: apiProduct.stock_status === 'in_stock' ? 'In Stock' : 'Out of Stock',
      category: apiProduct.category,
      description: apiProduct.description || '',
      productinfo: apiProduct.description || '',
      image: primaryImageUrl,
      additionalImages: additionalImages,
      // Specification fields
      warranty: apiProduct.warranty || '',
      power: apiProduct.power || '',
      voltage: apiProduct.voltage || '',
      efficiency: apiProduct.efficiency || '',
      capacity: apiProduct.capacity || '',
      cycles: apiProduct.cycles || '',
      model: apiProduct.model || '',
      dimensions: apiProduct.dimensions || '',
      weight: apiProduct.weight || '',
      // Store original for reference
      _original: apiProduct
    };
  };

  const formatPrice = (price) => {
    if (typeof price === 'number') {
      return `P ${price.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    }
    return price || 'P 0.00';
  };



  const handleAddToCart = () => {
    if (!product) return;
    
    // Check if user is logged in
    if (!user) {
      setShowLoginModal(true);
      return;
    }
    
    const cartProduct = {
      id: product.id,
      name: product.productname || product.name,
      price: product.discountprice || product.price,
      originalPrice: product.price,
      image: product.image,
      brand: typeof product.brand === 'object' ? (product.brand.display_name || product.brand.name || 'Unknown Brand') : (typeof product.brand === 'string' ? product.brand : 'Unknown Brand'),
      description: product.productinfo || product.description,
      sku: product.sku,
      category: typeof product.category === 'object' ? (product.category.display_name || product.category.name || 'Unknown Category') : (typeof product.category === 'string' ? product.category : 'Unknown Category'),
      quantity: quantity
    };

    addToCart(cartProduct);
    alert(`${quantity} ${cartProduct.name}(s) added to cart!`);
  };

  const handleWishlist = () => {
    if (!product) return;
    
    // Check if user is logged in
    if (!user) {
      setShowLoginModal(true);
      return;
    }
    
    // Add to wishlist logic would go here
    // For now, just show a message
    alert(`${product.productname || product.name} added to wishlist!`);
  };

  const handleBuyNow = () => {
    handleAddToCart();
    navigate('/checkout');
  };

  const handleQuantityChange = (change) => {
    const newQuantity = quantity + change;
    if (newQuantity >= 1) {
      setQuantity(newQuantity);
    }
  };

  const getImages = () => {
    if (!product) return [];
    return [product.image, ...(product.additionalImages || [])];
  };

  const getDiscountPercentage = () => {
    if (!product || !product.has_discount) return 0;
    if (product.discount_percentage) return product.discount_percentage;
    
    if (product.price > (product.discountprice || product.price)) {
      return Math.round(((product.price - (product.discountprice || product.price)) / product.price) * 100);
    }
    return 0;
  };

  if (loading) {
    return (
      <div className="loading-container">
        <div className="loading-spinner"></div>
        <p>Loading product details...</p>
      </div>
    );
  }

  if (error || !product) {
    return (
      <div className="not-found-container">
        <h2>{error || 'Product Not Found'}</h2>
        <p>The product you're looking for doesn't exist or has been removed.</p>
        <Link to="/products" className="back-to-products">
          View All Products
        </Link>
      </div>
    );
  }

  const images = getImages();
  const discountPercentage = getDiscountPercentage();
  const hasDiscount = discountPercentage > 0;
  const currentPrice = product.discountprice || product.price;
  const isInStock = product.stock_status === 'in_stock';

  return (
    <div className="product-detail-page">
      {/* Breadcrumb */}
      <div className="breadcrumb">
        <Link to="/">Home</Link> &gt; 
        <Link to="/products">Products</Link> &gt; 
        {/* {product.category && <Link to={`/products?category=${product.category}`}>{product.category}</Link>} &gt; */}
        <span>{product.productname || product.name}</span>
      </div>

      <div className="product-detail-container">
        {/* Product Images */}
        <div className="product-images">
          <div className="main-image">
            <img
              src={images[selectedImage]}
              alt={product.productname || product.name}
              className="product-main-image"
              onError={(e) => {
                e.target.onerror = null;
                e.target.src = '/images/placeholder.jpg';
              }}
            />
            {hasDiscount && (
              <div className="product-badge">
                <span className="discount-badge">-{discountPercentage}%</span>
              </div>
            )}
          </div>
          
          {images.length > 1 && (
            <div className="thumbnail-images">
              {images.map((img, index) => (
                <button
                  key={index}
                  className={`thumbnail-btn ${selectedImage === index ? 'active' : ''}`}
                  onClick={() => setSelectedImage(index)}
                >
                  <img
                    src={img}
                    alt={`${product.productname || product.name} view ${index + 1}`}
                    onError={(e) => {
                      e.target.onerror = null;
                      e.target.src = '/images/placeholder.jpg';
                    }}
                  />
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Product Info */}
        <div className="product-info">
          <div className="product-header">
            <span className="product-category">{product && typeof product.category === 'object' ? (product.category.display_name || product.category.name || 'Unknown Category') : product.category}</span>
            <h1 className="product-title">{product.productname || product.name}</h1>
            <div className="product-brand">
              Brand: <span>{product && typeof product.brand === 'object' ? (product.brand.display_name || product.brand.name || 'Unknown Brand') : product.brand}</span>
            </div>
          </div>

          <div className="product-price-section">
            <div className="product-price">
              <span className="current-price">{formatPrice(currentPrice)}</span>
              {hasDiscount && (
                <span className="original-price">{formatPrice(product.price)}</span>
              )}
            </div>
            <div className="product-stock">
              {isInStock ? (
                <span className="in-stock">✓ In Stock</span>
              ) : (
                <span className="out-of-stock">✗ Out of Stock</span>
              )}
              {product.sku && <span className="product-sku">SKU: {product.sku}</span>}
            </div>
          </div>

          {product.productinfo && (
            <div className="product-description">
              <h3>Description</h3>
              <p>{product.productinfo}</p>
            </div>
          )}

          <div className="product-specs">
            <h3>Product Details</h3>
            <div className="specs-grid">
              {product.model && (
                <div className="spec-item">
                  <span className="spec-label">Model:</span>
                  <span className="spec-value">{product.model}</span>
                </div>
              )}
              {product.power && (
                <div className="spec-item">
                  <span className="spec-label">Power:</span>
                  <span className="spec-value">{product.power}</span>
                </div>
              )}
              {product.capacity && (
                <div className="spec-item">
                  <span className="spec-label">Capacity:</span>
                  <span className="spec-value">{product.capacity}</span>
                </div>
              )}
              {product.voltage && (
                <div className="spec-item">
                  <span className="spec-label">Voltage:</span>
                  <span className="spec-value">{product.voltage}</span>
                </div>
              )}
              {product.efficiency && (
                <div className="spec-item">
                  <span className="spec-label">Efficiency:</span>
                  <span className="spec-value">{product.efficiency}</span>
                </div>
              )}
              {product.warranty && (
                <div className="spec-item">
                  <span className="spec-label">Warranty:</span>
                  <span className="spec-value">{product.warranty}</span>
                </div>
              )}
              {product.dimensions && (
                <div className="spec-item">
                  <span className="spec-label">Dimensions:</span>
                  <span className="spec-value">{product.dimensions}</span>
                </div>
              )}
              {product.weight && (
                <div className="spec-item">
                  <span className="spec-label">Weight:</span>
                  <span className="spec-value">{product.weight}</span>
                </div>
              )}
              {product.cycles && (
                <div className="spec-item">
                  <span className="spec-label">Cycles:</span>
                  <span className="spec-value">{product.cycles}</span>
                </div>
              )}
            </div>
          </div>

          {/* Add to Cart Section */}
          <div className="add-to-cart-section">
            <div className="quantity-selector">
              <label>Quantity:</label>
              <div className="quantity-controls">
                <button
                  className="quantity-btn"
                  onClick={() => handleQuantityChange(-1)}
                  disabled={quantity <= 1}
                >
                  -
                </button>
                <input
                  type="number"
                  min="1"
                  value={quantity}
                  onChange={(e) => {
                    const value = parseInt(e.target.value) || 1;
                    setQuantity(Math.max(1, value));
                  }}
                  className="quantity-input"
                />
                <button
                  className="quantity-btn"
                  onClick={() => handleQuantityChange(1)}
                >
                  +
                </button>
              </div>
            </div>


            <div className="action-buttons">
              <button
                className="add-to-cart-btn"
                onClick={handleAddToCart}
                disabled={!isInStock}
              >
                {isInStock ? `Add to Cart (${quantity})` : 'Out of Stock'}
              </button>
              <button
                className="buy-now-btn"
                onClick={handleBuyNow}
                disabled={!isInStock}
              >
                Buy Now
              </button>
              <button
                className="wishlist-btn"
                onClick={handleWishlist}
                disabled={!isInStock}
                title="Add to Wishlist"
              >
                ❤️
              </button>
            </div>

            {!isInStock && (
              <div className="out-of-stock-notice">
                This product is currently out of stock. Please check back later or contact us for availability.
              </div>
            )}

            <div className="product-meta">
              {product.category && (
                <div className="meta-item">
                  <span className="meta-label">Category:</span>
                  <span className="meta-value">
                    <Link to={`/products?category=${typeof product.category === 'object' ? (product.category.id || product.category.name || 'unknown') : product.category}`}>
                      {typeof product.category === 'object' ? (product.category.display_name || product.category.name || 'Unknown Category') : product.category}
                    </Link>
                  </span>
                </div>
              )}
              {product.brand && (
                <div className="meta-item">
                  <span className="meta-label">Brand:</span>
                  <span className="meta-value">
                    <Link to={`/products?brand=${typeof product.brand === 'object' ? (product.brand.id || product.brand.name || 'unknown') : product.brand}`}>
                      {typeof product.brand === 'object' ? (product.brand.display_name || product.brand.name || 'Unknown Brand') : product.brand}
                    </Link>
                  </span>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Related Products Section (Optional - you can implement this later) */}
      {product.category && (
        <div className="related-products-section">
          <h2>Related Products</h2>
          <p>Browse similar products in the <Link to={`/products?category=${typeof product.category === 'object' ? (product.category.id || product.category.name || 'unknown') : product.category}`}>
            {typeof product.category === 'object' ? (product.category.display_name || product.category.name || 'Unknown Category') : product.category}
          </Link> category.</p>
        </div>

      )}
      
      {/* Enhanced Login Modal */}
      <EnhancedLoginModal 
        isOpen={showLoginModal}
        onClose={() => setShowLoginModal(false)}
        title="Join KingSolarMan"
        subtitle="Log in or create an account to add items to your cart and wishlist"
        contextMessage="You need to be logged in to save items to your cart and wishlist. This ensures your items are saved across devices and you can track your order history."
        benefits={[
          { icon: "🛒", text: "Save your cart across devices" },
          { icon: "❤️", text: "Keep your wishlist forever" },
          { icon: "📋", text: "Track your order history" },
          { icon: "🎯", text: "Quick reordering of favorites" },
          { icon: "💰", text: "Exclusive member discounts" },
          { icon: "🚚", text: "Priority customer support" }
        ]}
        isMandatory={false}
      />
    </div>
  );
};

export default ProductDetail;
